import { useEffect, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Card } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Skeleton } from "@/components/ui/skeleton";
import { Lock } from "lucide-react";

const fmt = (n) => new Intl.NumberFormat("en-TZ").format(Math.round(n || 0));

export default function CreditorsDebtors() {
  const { canViewPage } = useAuth();
  const canView = canViewPage("creditors_debtors");
  const [debtors, setDebtors] = useState([]); // unpaid invoices
  const [creditors, setCreditors] = useState([]); // unpaid supplier POs
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!canView) { setLoading(false); return; }
    Promise.all([
      api.get("/invoices/", { params: { limit: 500 } }),
      api.get("/purchase-orders/", { params: { limit: 500 } }),
    ]).then(([inv, po]) => {
      setDebtors((inv.data.items || []).filter((i) =>
        // Proforma invoices aren't a real billing commitment, so they're
        // never a real receivable -- excluded here like everywhere else.
        ["unpaid_invoice", "partially_paid"].includes(String(i.status).toLowerCase()) && i.doc_type !== "Proforma Invoice"
      ));
      setCreditors((po.data.items || []).filter((i) => ["pending", "approved"].includes(String(i.status).toLowerCase())));
    }).finally(() => setLoading(false));
  }, [canView]);

  if (!canView) {
    return (
      <div className="flex flex-col items-center justify-center py-24 text-center" data-testid="module-forbidden">
        <Lock className="w-8 h-8 text-muted-foreground mb-3" />
        <p className="font-display text-lg font-medium">You don't have permission to view this page</p>
      </div>
    );
  }

  const outstanding = (inv) => ((inv.amount || 0) + (inv.tax || 0)) - (inv.amount_paid || 0);
  const totalDebt = debtors.reduce((a, b) => a + outstanding(b), 0);
  const totalCredit = creditors.reduce((a, b) => a + (b.amount || 0), 0);

  return (
    <div className="space-y-6" data-testid="creditors-debtors-page">
      <div>
        <p className="text-[11px] uppercase tracking-[0.28em] text-amber-500 font-semibold">Aging</p>
        <h1 className="font-display text-3xl sm:text-4xl font-semibold tracking-tight mt-1">Creditors & Debtors</h1>
        <p className="text-sm text-muted-foreground mt-1">Outstanding balances, aging and credit tracking.</p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Card className="p-6 border border-border rounded-xl bg-card">
          <p className="text-[11px] uppercase tracking-[0.2em] text-muted-foreground">Total Receivable (debtors)</p>
          <p className="font-display text-3xl font-semibold mt-2 text-amber-500">TZS {fmt(totalDebt)}</p>
          <p className="text-xs text-muted-foreground mt-1">{debtors.length} open invoices</p>
        </Card>
        <Card className="p-6 border border-border rounded-xl bg-card">
          <p className="text-[11px] uppercase tracking-[0.2em] text-muted-foreground">Total Payable (creditors)</p>
          <p className="font-display text-3xl font-semibold mt-2 text-rose-500">TZS {fmt(totalCredit)}</p>
          <p className="text-xs text-muted-foreground mt-1">{creditors.length} open POs</p>
        </Card>
      </div>

      <Tabs defaultValue="debtors">
        <TabsList>
          <TabsTrigger value="debtors">Debtors</TabsTrigger>
          <TabsTrigger value="creditors">Creditors</TabsTrigger>
        </TabsList>
        <TabsContent value="debtors">
          <Card className="rounded-xl border border-border bg-card overflow-hidden">
            {loading ? <div className="p-6"><Skeleton className="h-40" /></div> : (
              <Table>
                <TableHeader>
                  <TableRow className="bg-muted/40">
                    <TableHead>Invoice</TableHead><TableHead>Customer</TableHead>
                    <TableHead>Amount</TableHead><TableHead>Due</TableHead><TableHead>Status</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {debtors.length === 0 ? <TableRow><TableCell colSpan={5} className="text-center py-8 text-muted-foreground">No outstanding receivables</TableCell></TableRow> :
                    debtors.map((d) => (
                      <TableRow key={d.id}>
                        <TableCell className="font-medium">{d.invoice_no}</TableCell>
                        <TableCell>{d.customer}</TableCell>
                        <TableCell>TZS {fmt(outstanding(d))}</TableCell>
                        <TableCell>{d.due_date || "—"}</TableCell>
                        <TableCell><span className="text-xs px-2 py-0.5 rounded bg-amber-500/10 text-amber-600">{String(d.status).replace(/_/g, " ")}</span></TableCell>
                      </TableRow>
                    ))}
                </TableBody>
              </Table>
            )}
          </Card>
        </TabsContent>
        <TabsContent value="creditors">
          <Card className="rounded-xl border border-border bg-card overflow-hidden">
            {loading ? <div className="p-6"><Skeleton className="h-40" /></div> : (
              <Table>
                <TableHeader>
                  <TableRow className="bg-muted/40">
                    <TableHead>PO</TableHead><TableHead>Supplier</TableHead>
                    <TableHead>Amount</TableHead><TableHead>Status</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {creditors.length === 0 ? <TableRow><TableCell colSpan={4} className="text-center py-8 text-muted-foreground">No outstanding payables</TableCell></TableRow> :
                    creditors.map((c) => (
                      <TableRow key={c.id}>
                        <TableCell className="font-medium">{c.po_no}</TableCell>
                        <TableCell>{c.supplier}</TableCell>
                        <TableCell>TZS {fmt(c.amount)}</TableCell>
                        <TableCell><span className="text-xs px-2 py-0.5 rounded bg-rose-500/10 text-rose-500">{c.status}</span></TableCell>
                      </TableRow>
                    ))}
                </TableBody>
              </Table>
            )}
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
